import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { useCart } from "../context/CartContext";
import { Minus, Plus, X, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createCheckoutSession } from "../lib/api";
import { toast } from "sonner";

export const CartDrawer = () => {
    const { t } = useTranslation();
    const { items, open, setOpen, removeItem, updateQty, subtotal, clear } = useCart();
    const [checkingOut, setCheckingOut] = useState(false);
    const [email, setEmail] = useState("");

    const onCheckout = async () => {
        if (items.length === 0) return;
        setCheckingOut(true);
        try {
            const payload = {
                items: items.map((i) => ({ product_id: i.id, size: i.size, qty: i.qty })),
                origin_url: window.location.origin,
                email: email.trim() || null,
            };
            const session = await createCheckoutSession(payload);
            window.location.href = session.url;
        } catch (err) {
            toast.error(t("cart.could_not_start"));
            setCheckingOut(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
                side="right"
                data-testid="cart-drawer"
                className="w-full sm:max-w-md bg-[#0C0C0C] border-l border-white/10 text-[#F4F4F0] p-0 flex flex-col rounded-none"
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/10">
                    <SheetTitle className="text-display text-3xl text-left">
                        {t("cart.title")} <span className="text-[#A3A3A3] text-base align-middle">— {items.length} {items.length === 1 ? t("cart.items_one") : t("cart.items_other")}</span>
                    </SheetTitle>
                    <SheetDescription className="sr-only">{t("cart.title")}</SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full py-20 text-center gap-4">
                            <div className="text-overline text-[#666]">{t("cart.empty_label")}</div>
                            <p className="text-[#A3A3A3] max-w-[240px]">{t("cart.empty")}</p>
                            <Link to="/shop" onClick={() => setOpen(false)} className="btn-ghost mt-2" data-testid="empty-cart-shop-link">
                                {t("cart.browse_shop")}
                            </Link>
                        </div>
                    ) : (
                        <ul className="divide-y divide-white/[0.06]">
                            {items.map((item) => (
                                <li key={item.key} data-testid={`cart-item-${item.slug}-${item.size}`} className="py-5 flex gap-4">
                                    <Link to={`/product/${item.slug}`} onClick={() => setOpen(false)} className="block w-20 h-24 shrink-0 overflow-hidden bg-[#141414] border border-white/[0.06]">
                                        <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                                    </Link>
                                    <div className="flex-1 flex flex-col">
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <Link to={`/product/${item.slug}`} onClick={() => setOpen(false)} className="text-display text-lg leading-tight">{item.name}</Link>
                                                <div className="text-[11px] tracking-[0.18em] uppercase text-[#A3A3A3] mt-1 font-display">
                                                    {t("cart.size")} {item.size}
                                                    {item.color && <span> · {item.color}</span>}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeItem(item.key)}
                                                data-testid={`remove-item-${item.key}`}
                                                className="text-[#666] hover:text-[#F4F4F0]"
                                                aria-label={t("cart.remove")}
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                        <div className="mt-auto flex items-center justify-between">
                                            <div className="flex items-center border border-white/10">
                                                <button
                                                    onClick={() => updateQty(item.key, item.qty - 1)}
                                                    data-testid={`qty-decrease-${item.key}`}
                                                    className="p-2 hover:bg-white/[0.04]"
                                                    aria-label={t("cart.decrease_qty")}
                                                >
                                                    <Minus className="h-3 w-3" />
                                                </button>
                                                <span className="w-8 text-center text-sm" data-testid={`qty-value-${item.key}`}>{item.qty}</span>
                                                <button
                                                    onClick={() => updateQty(item.key, item.qty + 1)}
                                                    data-testid={`qty-increase-${item.key}`}
                                                    className="p-2 hover:bg-white/[0.04]"
                                                    aria-label={t("cart.increase_qty")}
                                                >
                                                    <Plus className="h-3 w-3" />
                                                </button>
                                            </div>
                                            <div className="text-brutal text-sm">${(item.price * item.qty).toFixed(0)}</div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-white/10 px-6 py-5 space-y-4">
                        <div className="space-y-1.5">
                            <label htmlFor="cart-email" className="text-overline text-[#A3A3A3]">
                                {t("cart.email_label")}
                            </label>
                            <input
                                id="cart-email"
                                type="email"
                                placeholder={t("cart.email_placeholder")}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="cart-email-input"
                                className="w-full bg-transparent border border-white/10 px-3 py-2.5 text-sm text-[#F4F4F0] placeholder:text-[#666] focus:outline-none focus:border-white/40"
                            />
                            <p className="text-[10px] text-[#666] leading-relaxed">
                                {t("cart.email_help")}
                            </p>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-overline text-[#A3A3A3]">{t("cart.subtotal")}</span>
                            <span className="text-display text-2xl" data-testid="cart-subtotal">${subtotal.toFixed(0)}</span>
                        </div>
                        <p className="text-xs text-[#666] leading-relaxed">
                            {t("cart.shipping_note")}
                        </p>
                        <button
                            className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="checkout-button"
                            onClick={onCheckout}
                            disabled={checkingOut}
                        >
                            {checkingOut ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> {t("cart.redirecting")}
                                </>
                            ) : (
                                <>
                                    {t("cart.checkout_button")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                                </>
                            )}
                        </button>
                        <button
                            onClick={clear}
                            data-testid="clear-cart"
                            className="w-full text-overline text-[#666] hover:text-[#F4F4F0] py-2"
                        >
                            {t("cart.clear_cart")}
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};
